import React, { useState, useEffect, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { Container, Card, CardBody, Button, Table, Badge, Input, Alert, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import MetaTags from "react-meta-tags";
import logo from "../../assets/images/logo/glogix-logo-white.png";

const inputStyle = {
  backgroundColor: "rgba(11, 17, 32, 0.9)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  color: "#fff",
};
const visibleButtonStyle = { color: "#e2e8f0", borderColor: "rgba(226, 232, 240, 0.45)" };

const ACTION_LABELS = {
  "admin:login_success": "Admin signed in",
  "admin:login_failed": "Admin sign-in failed",
  "admin:api_key_save": "API key saved",
  "admin:api_key_delete": "API key deleted",
  "admin:api_key_test": "API key tested",
  "admin:api_key_cache_refresh": "API key cache refreshed",
  "admin:alert_email_update": "Client alert email changed",
  "admin:alert_email_test": "Test email sent to client",
  "admin:mfa_policy_update": "Client 2-step policy changed",
  "admin:mfa_reset": "User 2-step verification reset",
};

const FILTERS = [
  { value: "", label: "All admin activity" },
  { value: "admin:login_success", label: "Admin sign-ins" },
  { value: "admin:login_failed", label: "Failed admin sign-ins" },
  { value: "admin:api_key*", label: "API keys (all)" },
  { value: "admin:alert_email*", label: "Client alert emails" },
  { value: "admin:mfa*", label: "Two-step verification changes" },
];

function formatDateTime(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function detailText(metadata) {
  if (!metadata || typeof metadata !== "object") return "";
  const text = Object.entries(metadata)
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
  return text.length > 220 ? `${text.slice(0, 220)}…` : text;
}

/**
 * GuardLogix admin portal: what GuardLogix administrators did (sign-ins, API keys, client settings). Read only.
 */
const AdminActivity = () => {
  const history = useHistory();
  const adminToken = localStorage.getItem("adminToken");
  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const apiBase = () => {
    const base = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";
    return base.endsWith("/") ? base : `${base}/`;
  };

  const load = useCallback(async () => {
    if (!adminToken) {
      history.push("/admin-login");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (action) params.append("action", action);
      if (search.trim()) params.append("search", search.trim());
      if (fromDate) params.append("from", fromDate);
      if (toDate) params.append("to", toDate);
      const res = await fetch(`${apiBase()}api/admin/audit?${params.toString()}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        history.push("/admin-login");
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.status) {
        setRows(body.logs || []);
        setTotal(body.total || 0);
        setTotalPages(body.totalPages || 1);
      } else {
        setError(body.error || "Could not load admin activity.");
      }
    } catch (e) {
      setError(e.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [adminToken, history, page, limit, action, search, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change goes back to the first page
  useEffect(() => {
    setPage(1);
  }, [action, search, fromDate, toDate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    history.push("/login");
  };

  const failed = (a) => String(a).includes("failed");

  return (
    <React.Fragment>
      <MetaTags>
        <title>Admin Activity | Glogix</title>
      </MetaTags>

      <div className="min-vh-100 py-4" style={{ backgroundColor: "#0d131f", color: "#e2e8f0", fontFamily: "inherit" }}>
        <Container fluid="lg">
          <div
            className="d-flex flex-wrap align-items-center justify-content-between p-3 mb-4 rounded-3 shadow"
            style={{ backgroundColor: "rgba(19, 26, 42, 0.9)", border: "1px solid rgba(255, 255, 255, 0.08)" }}
          >
            <div className="d-flex align-items-center gap-3">
              <img src={logo} alt="Glogix" height="34" />
              <div className="border-start border-secondary ps-3 ms-2">
                <h5 className="mb-0 text-white fw-bold d-flex align-items-center gap-2">
                  <FeatherIcon icon="activity" size={18} className="text-info" />
                  Admin Activity
                </h5>
                <span className="text-muted fs-12">What GuardLogix administrators did: sign-ins, API keys and client settings</span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
              <div className="d-none d-md-block me-2 text-end">
                <div className="text-white fs-13 fw-semibold">{adminUser.name || "Master Administrator"}</div>
                <div className="text-muted fs-11">{adminUser.email || "admin@guardlogix.com"}</div>
              </div>
              <Link to="/admin/clients" className="btn btn-outline-light btn-sm d-flex align-items-center gap-1" style={{ borderRadius: "6px", ...visibleButtonStyle }}>
                <FeatherIcon icon="users" size={14} />
                <span className="d-none d-sm-inline">Clients</span>
              </Link>
              <Link to="/admin/api-keys" className="btn btn-outline-light btn-sm d-flex align-items-center gap-1" style={{ borderRadius: "6px", ...visibleButtonStyle }}>
                <FeatherIcon icon="key" size={14} />
                <span className="d-none d-sm-inline">API Keys</span>
              </Link>
              <Button color="danger" size="sm" onClick={handleLogout} className="d-flex align-items-center gap-1" style={{ borderRadius: "6px" }}>
                <FeatherIcon icon="log-out" size={14} />
                <span className="d-none d-sm-inline">Exit Portal</span>
              </Button>
            </div>
          </div>

          {error && <Alert color="danger" className="fs-13">{error}</Alert>}

          <Card className="border-0 shadow-sm" style={{ backgroundColor: "#161f30", borderRadius: "10px" }}>
            <CardBody className="p-0">
              <div className="p-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="text-white fw-semibold">
                  {total} entr{total === 1 ? "y" : "ies"}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Input type="select" value={action} onChange={(e) => setAction(e.target.value)} style={{ ...inputStyle, maxWidth: "240px" }}>
                    {FILTERS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </Input>
                  <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ ...inputStyle, maxWidth: "160px" }} title="From" />
                  <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ ...inputStyle, maxWidth: "160px" }} title="To" />
                  <Input type="search" placeholder="Search person or target" value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...inputStyle, maxWidth: "240px" }} />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5"><Spinner color="info" /></div>
              ) : rows.length === 0 ? (
                <div className="text-center text-muted py-5 fs-13">No admin activity found.</div>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle mb-0 table-hover table-dark fs-13">
                    <thead style={{ backgroundColor: "#111827", textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                      <tr>
                        <th className="ps-4">When</th>
                        <th>Who</th>
                        <th>What</th>
                        <th>Target</th>
                        <th>Details</th>
                        <th className="pe-4">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r._id} style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}>
                          <td className="ps-4 text-nowrap">{formatDateTime(r.createdAt)}</td>
                          <td>{r.who}</td>
                          <td>
                            <Badge color={failed(r.action) ? "danger" : "info"} pill>{ACTION_LABELS[r.action] || r.action}</Badge>
                          </td>
                          <td className="text-break">{r.target || "—"}</td>
                          <td className="text-muted fs-12 text-break" style={{ maxWidth: "340px" }}>{detailText(r.metadata) || "—"}</td>
                          <td className="pe-4 text-nowrap text-muted fs-12">{r.ip || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <div className="p-3 d-flex align-items-center justify-content-between">
                <span className="text-muted fs-12">Page {page} of {totalPages}</span>
                <div className="d-flex gap-2">
                  <Button size="sm" color="outline-light" style={visibleButtonStyle} disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    Previous
                  </Button>
                  <Button size="sm" color="outline-light" style={visibleButtonStyle} disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdminActivity;
